import api from './api';
import qs from 'query-string';
import {
    InsuranceListingPersonSnapshot,
    InsuranceListingSnapshot,
} from '../models/Insurance/InsuranceListing';
import { keyBy } from '../lib/lists';
import { InsuranceStatus, InsuranceType } from '../constants/insurance';
import { PsInsuranceSnapshot } from '../models';
import { ArchivalAction } from '../constants/filter';
import { decamelizeKeys } from '../lib';
import {
    CountResponse,
    PaginatedRequest,
    PaginatedResponse,
} from '../lib/pagination';
import { getBackVersion } from '../config';

export type RawInsuranceListingResponse = Omit<
    InsuranceListingSnapshot,
    'people'
> & {
    people?: InsuranceListingPersonSnapshot[];
};

type RawInsurancePaginatedResponse = {
    insurances: RawInsuranceListingResponse[];
} & PaginatedResponse;

type InsurancePaginatedResponse = {
    insurances: InsuranceListingSnapshot[];
} & PaginatedResponse;

type InsuranceRequestFilter = {
    address?: string;
    name?: string;
    insuranceType?: InsuranceType;
    status?: InsuranceStatus[];
    isArchived?: boolean;
};

const toSnapshot = ({
    people,
    ...response
}: RawInsuranceListingResponse): InsuranceListingSnapshot => {
    return {
        ...response,
        people: people ? keyBy(people, x => x.document) : null,
    };
};

const mapToSnapshot = (response: RawInsuranceListingResponse[]) =>
    response.map(toSnapshot);

const getInsurances = (): Promise<InsuranceListingSnapshot[]> => {
    return api
        .request<RawInsuranceListingResponse[]>({
            method: 'GET',
            url: `${getBackVersion()}/agent/insurances`,
        })
        .then(mapToSnapshot);
};

const getPaginatedInsurances = (
    params: PaginatedRequest & InsuranceRequestFilter = {}
): Promise<InsurancePaginatedResponse> => {
    return api
        .request<RawInsurancePaginatedResponse>({
            method: 'GET',
            url: `${getBackVersion()}/agent/insurances`,
            params: decamelizeKeys({ ...params, paginate: true }),
            paramsSerializer: params => {
                // {a: [2, 3]} -> ?a=2&a=3
                // which is the supported format by the backend
                return qs.stringify(params, { arrayFormat: 'none' });
            },
        })
        .then(response => ({
            insurances: mapToSnapshot(response.insurances),
            after: response.after,
            count: response.count,
        }));
};

const getInsurancesCount = (
    params: PaginatedRequest & InsuranceRequestFilter
): Promise<CountResponse> => {
    return api.request<CountResponse>({
        method: 'GET',
        url: `${getBackVersion()}/agent/insurances/count`,
        params: decamelizeKeys(params),
    });
};

const createInsurance = (
    type: InsuranceType
): Promise<InsuranceListingSnapshot> => {
    return api
        .request<{ id: string }>({
            method: 'POST',
            url: `${getBackVersion()}/agent/insurances/${type}`,
        })
        .then(({ id }) =>
            api.request<RawInsuranceListingResponse>({
                method: 'GET',
                url: `${getBackVersion()}/agent/insurances/${id}`,
            })
        )
        .then(toSnapshot);
};

const archiveInsurance = (
    insuranceId: string,
    action: ArchivalAction
): Promise<void> => {
    return api.request({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/insurances/${insuranceId}`,
        params: { action },
    });
};

const setStep = (
    insuranceId: string,
    step: string
): Promise<PsInsuranceSnapshot> =>
    api.request<PsInsuranceSnapshot>({
        method: 'PATCH',
        url: `${getBackVersion()}/agent/insurances/${insuranceId}`,
        data: {
            // eslint-disable-next-line camelcase
            purchase_step: step,
        },
    });

export default {
    archiveInsurance,
    createInsurance,
    getInsurances,
    getInsurancesCount,
    getPaginatedInsurances,
    setStep,
};
export type { InsurancePaginatedResponse };
